import {
  Box,
  Button,
  Flex,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArticleTags,
  EntityTag,
  SlimTag,
  tagsApi,
} from '@texas/api/endpoints/tagsApi';
import { EntityType } from '@texas/api/endpoints/trackerApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { ErrorDetails } from './alert/ErrorDetails';
import { Icons } from './Icons';
import { CreateTagModal } from '../tags/CreateTagModal';
import { UpdateTagModal } from '../tags/UpdateTagModal';
import { SharedDisclosureProps } from './types';
import { TexasSelect } from './form/TexasSelect';
import { ReactSelectOption } from '@texas/types';
import { TagWithTooltip } from './TagWithTooltip';
import { useActiveSession } from '@texas/hooks/useSession';

export interface SetTagsProps extends SharedDisclosureProps {
  articleIds: number[] | null;
  onTagsChanged: () => void;
}

interface TagState {
  tag: SlimTag;
  originalArticleIds: number[];
  currentArticleIds: number[];
}

interface PendingChange {
  type: 'add' | 'remove';
  tagId: number;
  articleIds: number[];
}

export function SetTags({
  isOpen,
  onClose,
  articleIds,
  onTagsChanged,
}: SetTagsProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const session = useActiveSession();
  const userId = session.currentUser.id;

  const {
    isOpen: isCreateOpen,
    onClose: onCreateClose,
    onOpen: onCreateOpen,
  } = useDisclosure();

  const {
    isOpen: isUpdateOpen,
    onClose: onUpdateClose,
    onOpen: onUpdateOpen,
  } = useDisclosure();

  const [editingTagId, setEditingTagId] = useState<number | null>(null);

  const {
    data: allTags,
    refetch: refetchAllTags,
    loading: loadingAllTags,
    error: allTagsError,
  } = useApiResource(tagsApi.getAll);

  const {
    data: articleTags,
    refetch: refetchArticleTags,
    loading: loadingArticleTags,
    error: articleTagsError,
  } = useApiResource(tagsApi.getArticleTags);

  const {
    request: linkToTagRequest,
    loading: linkingTag,
    error: linkError,
  } = useApiRequest(tagsApi.linkToTag);

  const {
    request: removeTagLinksRequest,
    loading: removingTag,
    error: removeError,
  } = useApiRequest(tagsApi.removeTagLinks);

  const [tagStates, setTagStates] = useState<Map<number, TagState>>(new Map());

  const uniqueArticleIds = useMemo(() => {
    if (!articleIds) return [];
    return [...new Set(articleIds)];
  }, [articleIds]);

  const totalArticleCount = uniqueArticleIds.length;

  useEffect(() => {
    if (isOpen && uniqueArticleIds.length > 0) {
      refetchAllTags({
        includeArchived: false,
        includeSystemTags: false,
        filterByEntityType: EntityType.Article,
      });
      refetchArticleTags({ articleIds: uniqueArticleIds });
    }
  }, [isOpen, uniqueArticleIds, refetchAllTags, refetchArticleTags]);

  useEffect(() => {
    if (!articleTags) return;

    const initialTagStates = new Map<number, TagState>();
    articleTags.forEach((article: ArticleTags) => {
      article.tags.forEach((tag: SlimTag) => {
        const existing = initialTagStates.get(tag.id);
        if (existing) {
          existing.originalArticleIds.push(article.articleId);
          existing.currentArticleIds.push(article.articleId);
        } else {
          initialTagStates.set(tag.id, {
            tag,
            originalArticleIds: [article.articleId],
            currentArticleIds: [article.articleId],
          });
        }
      });
    });
    setTagStates(initialTagStates);
  }, [articleTags]);

  const pendingChanges = useMemo((): PendingChange[] => {
    const changes: PendingChange[] = [];

    tagStates.forEach((state, tagId) => {
      const originalSet = new Set(state.originalArticleIds);
      const currentSet = new Set(state.currentArticleIds);

      const toAdd = state.currentArticleIds.filter(
        (id) => !originalSet.has(id),
      );
      const toRemove = state.originalArticleIds.filter(
        (id) => !currentSet.has(id),
      );

      if (toAdd.length > 0) {
        changes.push({ type: 'add', tagId, articleIds: toAdd });
      }
      if (toRemove.length > 0) {
        changes.push({ type: 'remove', tagId, articleIds: toRemove });
      }
    });

    return changes;
  }, [tagStates]);

  const hasChanges = pendingChanges.length > 0;

  const sortedTagStates = useMemo(() => {
    return Array.from(tagStates.values()).sort((a, b) =>
      a.tag.label.localeCompare(b.tag.label),
    );
  }, [tagStates]);

  const availableTagsToAdd = useMemo((): ReactSelectOption[] => {
    if (!allTags) return [];

    return allTags
      .filter((tag) => !tagStates.has(tag.id))
      .map((tag) => ({
        label: tag.label,
        value: tag.id,
      }));
  }, [allTags, tagStates]);

  const handleAddToAll = useCallback(
    (tagId: number) => {
      setTagStates((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(tagId);
        if (existing) {
          newMap.set(tagId, {
            ...existing,
            currentArticleIds: [...uniqueArticleIds],
          });
        }
        return newMap;
      });
    },
    [uniqueArticleIds],
  );

  const handleRemoveFromAll = useCallback((tagId: number) => {
    setTagStates((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(tagId);
      if (existing) {
        newMap.set(tagId, {
          ...existing,
          currentArticleIds: [],
        });
      }
      return newMap;
    });
  }, []);

  const handleAddNewTag = useCallback(
    (tagId: number) => {
      const tag = allTags?.find((t) => t.id === tagId);
      if (!tag) return;

      setTagStates((prev) => {
        const newMap = new Map(prev);
        newMap.set(tagId, {
          tag: {
            id: tag.id,
            label: tag.label,
            color: tag.color,
            userId: tag.userId,
          },
          originalArticleIds: [],
          currentArticleIds: [...uniqueArticleIds],
        });
        return newMap;
      });
    },
    [allTags, uniqueArticleIds],
  );

  const handleTagCreated = useCallback(
    (tag: EntityTag) => {
      setTagStates((prev) => {
        const newMap = new Map(prev);
        newMap.set(tag.id, {
          tag: {
            id: tag.id,
            label: tag.label,
            color: tag.color,
            userId: tag.userId,
          },
          originalArticleIds: [],
          currentArticleIds: [...uniqueArticleIds],
        });
        return newMap;
      });
      refetchAllTags({
        includeArchived: false,
        filterByEntityType: EntityType.Article,
      });
    },
    [uniqueArticleIds, refetchAllTags],
  );

  const handleEditTag = useCallback(
    (tagId: number) => {
      setEditingTagId(tagId);
      onUpdateOpen();
    },
    [onUpdateOpen],
  );

  const handleTagUpdated = useCallback(
    (updatedTag: EntityTag) => {
      setTagStates((prev) => {
        const newMap = new Map(prev);
        const existing = newMap.get(updatedTag.id);
        if (existing) {
          newMap.set(updatedTag.id, {
            ...existing,
            tag: {
              id: updatedTag.id,
              label: updatedTag.label,
              color: updatedTag.color,
              userId: updatedTag.userId,
            },
          });
        }
        return newMap;
      });
      refetchAllTags({
        includeArchived: false,
        filterByEntityType: EntityType.Article,
      });
    },
    [refetchAllTags],
  );

  const handleSave = useCallback(async () => {
    if (pendingChanges.length === 0) {
      onClose();
      return;
    }

    for (const change of pendingChanges) {
      if (change.type === 'add') {
        await linkToTagRequest({
          tagId: change.tagId,
          entityIds: change.articleIds,
          entityType: EntityType.Article,
        });
      } else {
        await removeTagLinksRequest({
          tagId: change.tagId,
          entityIds: change.articleIds,
          entityType: EntityType.Article,
        });
      }
    }

    if (!linkError && !removeError) {
      toast({
        title: t('general.updated'),
        status: 'success',
        isClosable: true,
      });
      onTagsChanged();
      onClose();
    }
  }, [
    pendingChanges,
    linkToTagRequest,
    removeTagLinksRequest,
    linkError,
    removeError,
    toast,
    t,
    onTagsChanged,
    onClose,
  ]);

  const isLoading = linkingTag || removingTag;
  const isLoadingData = loadingAllTags || loadingArticleTags;
  const error = allTagsError ?? articleTagsError;

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalCloseButton />
          <ModalHeader>{t('article.setTags')}</ModalHeader>
          <ModalBody>
            {isLoadingData && (
              <Flex justifyContent="center" p={4}>
                <Spinner />
              </Flex>
            )}
            {error && <ErrorDetails error={error} />}
            {linkError && <ErrorDetails error={linkError} />}
            {removeError && <ErrorDetails error={removeError} />}

            {!isLoadingData && (
              <VStack align="stretch" spacing={4}>
                <Box>
                  <Text fontWeight="bold" mb={2}>
                    {t('tags.currentTagsOnArticles')}
                  </Text>
                  {sortedTagStates.length === 0 ? (
                    <Text color="gray.500">{t('tags.noTagsAssigned')}</Text>
                  ) : (
                    <VStack align="stretch" spacing={2}>
                      {sortedTagStates.map((tagState) => {
                        const currentCount = tagState.currentArticleIds.length;
                        const isAllSelected =
                          currentCount === totalArticleCount;
                        const isNoneSelected = currentCount === 0;

                        const hasChanged = hasArticleIdsChanged(
                          tagState.originalArticleIds,
                          tagState.currentArticleIds,
                        );

                        return (
                          <Flex
                            key={tagState.tag.id}
                            p={2}
                            borderRadius="md"
                            bg={hasChanged ? 'whiteAlpha.100' : 'transparent'}
                            _light={{
                              bg: hasChanged ? 'blackAlpha.100' : 'transparent',
                            }}
                            alignItems="center"
                            justifyContent="space-between"
                          >
                            <Flex alignItems="center" gap={2}>
                              <TagWithTooltip tag={tagState.tag} />
                              {tagState.tag.userId === userId && (
                                <IconButton
                                  aria-label={t('tags.edit')}
                                  icon={<Icons.Pencil />}
                                  size="xs"
                                  variant="ghost"
                                  onClick={() => handleEditTag(tagState.tag.id)}
                                />
                              )}
                              <Text fontSize="sm" color="gray.500">
                                ({currentCount}/
                                {t('tags.articlesWithTag', {
                                  count: totalArticleCount,
                                })}
                                )
                              </Text>
                            </Flex>
                            <Flex gap={2}>
                              {!isAllSelected && (
                                <Button
                                  size="xs"
                                  variant="texas-outline-light"
                                  onClick={() =>
                                    handleAddToAll(tagState.tag.id)
                                  }
                                  isDisabled={isLoading}
                                >
                                  {t('tags.addToAll')}
                                </Button>
                              )}
                              {!isNoneSelected && (
                                <Button
                                  size="xs"
                                  variant="texas-outline-light"
                                  colorScheme="red"
                                  onClick={() =>
                                    handleRemoveFromAll(tagState.tag.id)
                                  }
                                  isDisabled={isLoading}
                                >
                                  {t('tags.removeFromAll')}
                                </Button>
                              )}
                            </Flex>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}
                </Box>

                <Box>
                  <Text fontWeight="bold" mb={2}>
                    {t('tags.addTag')}
                  </Text>
                  <Flex gap={2}>
                    <Box flex={1}>
                      <TexasSelect
                        placeholder={
                          availableTagsToAdd.length > 0
                            ? t('tags.selectTagToAdd')
                            : t('tags.noTagsAvailable')
                        }
                        options={availableTagsToAdd}
                        isDisabled={
                          isLoading || availableTagsToAdd.length === 0
                        }
                        onChange={(option) => {
                          if (option) {
                            handleAddNewTag(option.value as number);
                          }
                        }}
                        value={null}
                      />
                    </Box>
                    <Button
                      leftIcon={<Icons.Plus />}
                      variant="texas-outline-light"
                      onClick={onCreateOpen}
                      isDisabled={isLoading}
                    >
                      {t('tags.createNew')}
                    </Button>
                  </Flex>
                </Box>
              </VStack>
            )}
          </ModalBody>
          <ModalFooter
            backgroundColor="gray.800"
            _light={{ backgroundColor: 'gray.50' }}
          >
            <Button
              variant="texas-solid"
              onClick={handleSave}
              isLoading={isLoading}
              isDisabled={isLoading || !hasChanges}
            >
              {t('general.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <CreateTagModal
        isOpen={isCreateOpen}
        onClose={onCreateClose}
        onTagCreated={handleTagCreated}
      />

      {editingTagId && (
        <UpdateTagModal
          isOpen={isUpdateOpen}
          onClose={onUpdateClose}
          tagId={editingTagId}
          onTagUpdated={handleTagUpdated}
        />
      )}
    </>
  );
}

function hasArticleIdsChanged(
  originalIds: number[],
  currentIds: number[],
): boolean {
  try {
    const sortedOriginal = [...originalIds].sort();
    const sortedCurrent = [...currentIds].sort();

    if (sortedOriginal.length !== sortedCurrent.length) {
      return true;
    }

    for (let i = 0; i < sortedOriginal.length; i += 1) {
      if (sortedOriginal[i] !== sortedCurrent[i]) {
        return true;
      }
    }

    return false;
  } catch {
    return false;
  }
}
